import { CogIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from 'components/atoms/Button';
import { useAIChatContext } from 'components/organisms/AIChat/AIChat.context';
import useAIUsageConsent from 'hooks/shared/useAIUsageConsent';
import { useCallback, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import themeColors from 'theme/themeColors.json';
import {
  ConversationMessageToolConsentEnum,
  type ConversationMessagePart,
  type ConversationMessageToolPart,
} from 'types/ai.types';
import {
  AIFeatureUsageConsentEnum,
  AIToolUsageConsentEnum,
  type AIToolConsent,
} from 'types/users.types';
import AIToolConsentModal from '../AIToolConsentModal';
import usePatchConversationMessage from './usePatchConversationMessage';
import useToolContent from './useToolContent';
import { logAmplitude } from 'utils/amplitude';
import {
  WEB_AIASSISTANT_TOOLALLOWTHISTIME,
  WEB_AIASSISTANT_TOOLDENYTHISTIME,
} from 'utils/amplitude/events/ai.amplitude';

type AIMessagePartToolProps = {
  messageId: string;
  messagePart: ConversationMessageToolPart;
  messageParts: ConversationMessagePart[];
  isLastPart?: boolean;
};

const toolColorMap = {
  list_expenses: themeColors.expenses['700'],
  list_revenues: themeColors.revenues['700'],
  get_aggregated_expenses: themeColors.expenses['700'],
  get_aggregated_revenues: themeColors.revenues['700'],
  get_financial_summary: themeColors.taxes['700'],
  get_available_balance: themeColors.bank['700'],
  get_expense_benchmarks: themeColors.expenses['700'],
  get_revenue_benchmarks: themeColors.revenues['700'],
  fetch_user_taxes: themeColors.taxes['700'],
};

const AIMessagePartTool: FC<AIMessagePartToolProps> = ({
  messageId,
  messagePart,
  messageParts,
  isLastPart,
}) => {
  const { isStreaming, isLoading, handleRegenerateMessage, conversationId } =
    useAIChatContext();
  const { t } = useTranslation();

  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { patchConsents } = useAIUsageConsent();

  const { patchMessage } = usePatchConversationMessage(
    conversationId,
    messageId,
  );

  const shouldShowShimmerAnimation = useMemo(
    () =>
      messagePart.consent === ConversationMessageToolConsentEnum.required ||
      (isStreaming && isLastPart),
    [isStreaming, isLastPart, messagePart.consent],
  );

  const handleOpenConsentModal = useCallback(
    () => setIsConsentModalOpen(true),
    [],
  );

  const handleCloseConsentModal = useCallback(
    () => setIsConsentModalOpen(false),
    [],
  );

  const getNewMessageParts = useCallback(
    (consent: ConversationMessageToolConsentEnum) => {
      return messageParts.map((part) =>
        part.type === 'tool' && part.call_id === messagePart.call_id
          ? { ...part, consent }
          : part,
      );
    },
    [messageParts, messagePart],
  );

  const patchMessageAndRegenerate = useCallback(
    async (consent: ConversationMessageToolConsentEnum) => {
      setIsSubmitted(true);

      const parts = getNewMessageParts(consent);
      await patchMessage({
        parts,
        allowed_data_access_on_form:
          consent === ConversationMessageToolConsentEnum.allowed,
      });

      handleRegenerateMessage(messageId, parts);
    },
    [getNewMessageParts, patchMessage, handleRegenerateMessage, messageId],
  );

  const allowOnceAndProceed = useCallback(async () => {
    logAmplitude(WEB_AIASSISTANT_TOOLALLOWTHISTIME, {
      tool: messagePart.name,
      consents: messagePart.consent_types?.join(',') || '',
    });

    await patchMessageAndRegenerate(ConversationMessageToolConsentEnum.allowed);
  }, [messagePart.name, messagePart.consent_types, patchMessageAndRegenerate]);

  const handleDenyAndProceed = useCallback(async () => {
    logAmplitude(WEB_AIASSISTANT_TOOLDENYTHISTIME, {
      tool: messagePart.name,
      consents: messagePart.consent_types?.join(',') || '',
    });

    await patchMessageAndRegenerate(ConversationMessageToolConsentEnum.denied);
  }, [messagePart.name, messagePart.consent_types, patchMessageAndRegenerate]);

  const shouldShowConsentUI =
    isLastPart &&
    !isLoading &&
    !isStreaming &&
    messagePart.consent === ConversationMessageToolConsentEnum.required &&
    !isSubmitted;

  const handleConsentSave = useCallback(
    async (consents: AIToolConsent) => {
      await patchConsents({
        ...consents,
        ai_tax_advisor: AIFeatureUsageConsentEnum.allow,
      });

      const anyConsentAllowed = messagePart.consent_types?.some(
        (consent) => consents[consent] === AIToolUsageConsentEnum.allow,
      );

      if (anyConsentAllowed) {
        allowOnceAndProceed();
      } else {
        handleDenyAndProceed();
      }

      setIsConsentModalOpen(false);
    },
    [
      patchConsents,
      messagePart.consent_types,
      allowOnceAndProceed,
      handleDenyAndProceed,
    ],
  );

  const content = useToolContent(messagePart, !!isLastPart);

  const color =
    toolColorMap[messagePart.name as keyof typeof toolColorMap] ||
    themeColors.taxes['700'];

  return (
    <>
      <div className="max-w-[80%] py-2 rounded-10 self-start flex flex-col gap-2">
        <p
          className={classNames('text-sm text-primary-600', {
            'text-shimmer-animation': shouldShowShimmerAnimation,
          })}
          style={{ '--shimmer-base-color': color }}
        >
          {content}
        </p>

        <AnimatePresence>
          {shouldShowConsentUI && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
              className="flex flex-row gap-3"
            >
              <div className="flex flex-row">
                <Button
                  structure="primary"
                  color="taxes"
                  size="sm"
                  className="!min-w-0 rounded-r-none border-r-1 border-r-taxes-700"
                  onClick={handleOpenConsentModal}
                  disabled={isLoading}
                  icon={CogIcon}
                />

                <Button
                  structure="primary"
                  color="taxes"
                  size="sm"
                  className="rounded-l-none"
                  onClick={allowOnceAndProceed}
                  disabled={isLoading}
                >
                  {t('ai_bot.tool_consent.allow_once')}
                </Button>
              </div>
              <Button
                structure="secondary"
                size="sm"
                onClick={handleDenyAndProceed}
                color="primary"
                disabled={isLoading}
                className="!border-primary-100"
              >
                {t('ai_bot.tool_consent.deny')}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AIToolConsentModal
        isOpen={isConsentModalOpen}
        onClose={handleCloseConsentModal}
        onSave={handleConsentSave}
      />
    </>
  );
};

export default AIMessagePartTool;
